/**
 * useAccess — the caller's RBAC capabilities, for gating UI (RBAC Phase 4).
 *
 * Fetches `/v1/rbac/me` (capabilities + teams + enabled features the backend
 * resolved for this user) and exposes a `can(capability)` predicate. Every
 * RBAC-aware screen/nav item gates on this so the UI never offers an action
 * the backend would 403 — the backend remains the source of truth; this only
 * decides what to *show*.
 *
 * Pure types + logic live in ./access (no config/React deps, unit-testable).
 */
import { useQuery } from '@tanstack/react-query';

import { config } from '../config';
import {
  canWith,
  hasFeatureWith,
  type AccessMe,
  type CapabilityValue,
  type TeamRef,
} from './access';

export { Capability } from './access';
export type { AccessMe, CapabilityValue, TeamRef } from './access';

export const accessKeys = {
  me: ['rbac', 'me'] as const,
};

async function fetchMe(): Promise<AccessMe> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${config.API_URL}/v1/rbac/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to load access profile (${res.status})`);
  }
  return res.json();
}

export interface UseAccessResult {
  access: AccessMe | undefined;
  isLoading: boolean;
  /** True when the user holds `capability` (firm-wide or in any team). */
  can: (capability: CapabilityValue) => boolean;
  /** True when the org has `feature` enabled. */
  hasFeature: (feature: string) => boolean;
  teamIds: string[];
  /** The caller's own teams with names (for scope pickers). */
  teams: TeamRef[];
}

export function useAccess(): UseAccessResult {
  const { data, isLoading } = useQuery<AccessMe, Error>({
    queryKey: accessKeys.me,
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000, // capabilities change rarely; refetch is cheap
  });

  return {
    access: data,
    isLoading,
    can: (capability) => canWith(data, capability),
    hasFeature: (feature) => hasFeatureWith(data, feature),
    teamIds: data?.teamIds ?? [],
    teams: data?.teams ?? [],
  };
}
